import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Typography,
  Checkbox,
  Row,
  Col,
  Card,
} from "antd";
import { UserOutlined, MailOutlined, LockOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "react-query";
import { AlertService } from "../../services/alert.service";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { createAccount } from "../../services/accounts.service";
import { getPlans } from "../../services/subscriptions.service";
import { formatCurrency } from "../../services/utils";

export default function Register() {
  const [formRegister] = Form.useForm();
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  const navigate = useNavigate();

  const { Title, Text, Link: AntLink } = Typography;

  const { data: plansData, isLoading: plansLoading } = useQuery(
    "plans",
    getPlans
  );

  const { mutate: register, isLoading: registerLoading } = useMutation(
    createAccount,
    {
      onSuccess: () => {
        AlertService.sendAlert([
          {
            text: "Sua conta foi criada com sucesso. Enviamos um link de confirmação de cadastro para o e-mail informado.",
            type: "success",
            nextPage: true,
          },
        ]);

        return window.location.replace("/login");
      },
    }
  );

  const onFinish = async () => {
    if (!termsAccepted) {
      return;
    }

    if (!selectedPlanId) {
      AlertService.sendAlert([
        { text: "Selecione um plano para continuar.", type: "error" },
      ]);
      return;
    }

    formRegister
      .validateFields()
      .then((values) => {
        values.name = values.name.trim();
        values.email = values.email.trim();
        values.password = values.password.trim();
        values.password_confirmation = values.password_confirmation.trim();
        values.terms = 1;
        values.subscription_plan_id = selectedPlanId;

        register(values);
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  useEffect(() => {
    formRegister.setFieldValue("terms", termsAccepted);
  }, [termsAccepted]);

  return (
    <div style={{ padding: "0 10px" }}>
      <div style={{ textAlign: "center", marginBottom: 30 }}>
        <Title level={2} style={{ marginBottom: 10 }}>
          Crie sua conta
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Preencha os dados abaixo para começar
        </Text>
      </div>

      <Form
        onFinish={onFinish}
        form={formRegister}
        autoComplete="off"
        layout="vertical"
        size="large"
        requiredMark={false}
      >
        <Form.Item
          name="name"
          label="Nome/Empresa"
          style={{ marginBottom: 10 }}
          rules={[{ required: true, message: "Campo obrigatório." }]}
        >
          <Input
            prefix={<UserOutlined className="site-form-item-icon" />}
            placeholder="Seu nome ou da empresa"
          />
        </Form.Item>

        <Form.Item
          name="email"
          label="E-mail"
          style={{ marginBottom: 10 }}
          rules={[
            { required: true, message: "Por favor insira um e-mail válido." },
            { type: "email", message: "Formato de email inválido." },
          ]}
        >
          <Input
            prefix={<MailOutlined className="site-form-item-icon" />}
            placeholder="seu@email.com"
            type="email"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Crie uma senha"
          style={{ marginBottom: 10 }}
          rules={[
            { required: true, message: "Campo obrigatório" },
            {
              min: 8,
              message: "Deve ter no mínimo 8 caracteres.",
            },
            {
              pattern: new RegExp(
                "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[-+_!@#$%^&*.,?]).+$"
              ),
              message: `A senha deve conter maiúscula, minúscula, número e caractere especial`,
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="Sua senha"
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          name="password_confirmation"
          label="Confirme sua senha"
          dependencies={["password"]}
          style={{ marginBottom: 10 }}
          rules={[
            { required: true, message: "Campo obrigatório" },
            ({ getFieldValue }) => ({
              validator: (_, value) => {
                if (!value || value === getFieldValue("password")) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error("As senhas não coincidem."));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="Confirme sua senha"
            maxLength={100}
          />
        </Form.Item>

        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <Text strong style={{ fontSize: 16 }}>
            Selecione um plano:
          </Text>
          <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
            {plansData?.data?.map((plan: any) => (
              <Col span={12} xs={24} sm={12} key={plan.id}>
                <Card
                  hoverable
                  onClick={() => setSelectedPlanId(plan.id)}
                  style={{
                    borderColor:
                      selectedPlanId === plan.id ? "#1890ff" : "#f0f0f0",
                    borderWidth: selectedPlanId === plan.id ? 2 : 1,
                    backgroundColor:
                      selectedPlanId === plan.id ? "#f0f8ff" : "#ffffff",
                    height: "100%",
                    cursor: "pointer",
                  }}
                  bodyStyle={{ padding: 15 }}
                >
                  <Title level={4} style={{ marginBottom: 5, fontSize: 16 }}>
                    {plan.name}
                  </Title>
                  <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                    {formatCurrency(plan.price_monthly)}
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, fontWeight: 400 }}
                    >
                      /mês
                    </Text>
                  </Title>

                  <ul
                    style={{
                      paddingLeft: 15,
                      marginTop: 10,
                      fontSize: 12,
                      color: "#666",
                    }}
                  >
                    {plan.features &&
                      Object.entries(plan.features).map(([key, value]) => (
                        <li key={key} style={{ marginBottom: 2 }}>
                          {String(value)}
                        </li>
                      ))}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Form.Item name="terms" valuePropName="checked">
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          >
            Aceito os <Link to={"/terms"}>Termos e Condições</Link> e{" "}
            <Link to={"/privacy"}>Privacidade</Link>.
          </Checkbox>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={registerLoading}
            style={{ height: 45, fontSize: 16 }}
          >
            Criar conta
          </Button>
        </Form.Item>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Text>Já tem uma conta? </Text>
          <AntLink
            style={{ fontWeight: "bold" }}
            onClick={() => navigate("/login")}
          >
            Faça login
          </AntLink>
        </div>
      </Form>
    </div>
  );
}
